import React from 'react';
import { useFeatureFlag } from '../../../hooks/useFeatureFlags';

export const HolydaysHeaderSpringLayer: React.FC = () => {
  const { isEnabled } = useFeatureFlag('header_spring');

  if (!isEnabled) return null;

  return (
    <div className="header-spring" data-testid="header-spring" aria-hidden="true">
      <svg
        className="header-spring__svg"
        width="100%"
        height="100%"
        viewBox="0 0 1000 80"
        preserveAspectRatio="xMidYMid slice"
        focusable="false"
      >
        <defs>
          {/* Лепесток сакуры */}
          <symbol id="plng-sakura-petal" viewBox="0 0 24 24">
            <g transform="translate(12 12)">
              <path
                d="M0,-8 C3,-6 4,-2 0,0 C-4,-2 -3,-6 0,-8Z"
                fill="rgba(255,200,210,0.95)"
                stroke="rgba(255,255,255,0.7)"
                strokeWidth="0.5"
              />
            </g>
          </symbol>

          {/* Цветок сакуры — 5 лепестков */}
          <symbol id="plng-sakura-flower" viewBox="0 0 24 24">
            <g transform="translate(12 12)">
              {[0, 72, 144, 216, 288].map((deg) => (
                <path
                  key={deg}
                  d="M0,-6.5 C2,-5 2.5,-2 0,0 C-2.5,-2 -2,-5 0,-6.5Z"
                  fill="rgba(255,210,220,0.9)"
                  stroke="rgba(255,255,255,0.75)"
                  strokeWidth="0.5"
                  transform={`rotate(${deg})`}
                />
              ))}
              <circle cx="0" cy="0" r="1.6" fill="rgba(255,240,200,0.95)" stroke="none" />
            </g>
          </symbol>

          {/* Маленький цветочек — 4 лепестка */}
          <symbol id="plng-spring-flower-sm" viewBox="0 0 24 24">
            <g transform="translate(12 12)">
              {[0, 90, 180, 270].map((deg) => (
                <ellipse
                  key={deg}
                  cx="0"
                  cy="-3.5"
                  rx="1.8"
                  ry="3"
                  fill="rgba(255,255,255,0.9)"
                  stroke="rgba(255,200,210,0.6)"
                  strokeWidth="0.3"
                  transform={`rotate(${deg})`}
                />
              ))}
              <circle cx="0" cy="0" r="1.3" fill="rgba(255,235,170,0.95)" stroke="none" />
            </g>
          </symbol>

          {/* Листочек */}
          <symbol id="plng-spring-leaf" viewBox="0 0 24 24">
            <g transform="translate(12 12)">
              <path
                d="M0,-6 C3.5,-3.5 3.5,1.5 0,4 C-3.5,1.5 -3.5,-3.5 0,-6Z"
                fill="rgba(170,255,170,0.8)"
                stroke="rgba(255,255,255,0.5)"
                strokeWidth="0.4"
              />
              <line
                x1="0" y1="-4" x2="0" y2="3"
                stroke="rgba(255,255,255,0.5)"
                strokeWidth="0.3"
                strokeLinecap="round"
              />
            </g>
          </symbol>

          <pattern
            id="plng-header-spring-pattern"
            width="160"
            height="80"
            patternUnits="userSpaceOnUse"
          >
            {/* Ряд 1 — верх */}
            <use href="#plng-sakura-flower" x="2" y="2" width="22" height="22" opacity="0.6" />
            <use href="#plng-spring-flower-sm" x="34" y="6" width="16" height="16" opacity="0.55" transform="rotate(12 42 14)" />
            <use href="#plng-sakura-petal" x="60" y="4" width="12" height="12" opacity="0.65" transform="rotate(30 66 10)" />
            <use href="#plng-spring-flower-sm" x="80" y="0" width="18" height="18" opacity="0.5" transform="rotate(-15 89 9)" />
            <use href="#plng-sakura-petal" x="108" y="6" width="10" height="10" opacity="0.6" transform="rotate(-20 113 11)" />
            <use href="#plng-sakura-flower" x="124" y="2" width="20" height="20" opacity="0.5" transform="rotate(25 134 12)" />

            {/* Ряд 2 — середина */}
            <use href="#plng-sakura-petal" x="10" y="26" width="11" height="11" opacity="0.55" transform="rotate(-25 15.5 31.5)" />
            <use href="#plng-spring-leaf" x="30" y="28" width="14" height="14" opacity="0.5" transform="rotate(15 37 35)" />
            <use href="#plng-sakura-flower" x="54" y="24" width="20" height="20" opacity="0.55" transform="rotate(-10 64 34)" />
            <use href="#plng-sakura-petal" x="82" y="30" width="10" height="10" opacity="0.6" transform="rotate(45 87 35)" />
            <use href="#plng-spring-flower-sm" x="100" y="26" width="16" height="16" opacity="0.5" transform="rotate(8 108 34)" />
            <use href="#plng-spring-leaf" x="128" y="30" width="12" height="12" opacity="0.45" transform="rotate(-30 134 36)" />
            <use href="#plng-sakura-petal" x="148" y="28" width="10" height="10" opacity="0.5" transform="rotate(60 153 33)" />

            {/* Ряд 3 — низ */}
            <use href="#plng-spring-flower-sm" x="0" y="52" width="18" height="18" opacity="0.5" transform="rotate(20 9 61)" />
            <use href="#plng-sakura-petal" x="26" y="56" width="12" height="12" opacity="0.6" transform="rotate(-40 32 62)" />
            <use href="#plng-sakura-flower" x="48" y="50" width="22" height="22" opacity="0.5" transform="rotate(10 59 61)" />
            <use href="#plng-spring-leaf" x="76" y="54" width="14" height="14" opacity="0.45" transform="rotate(-20 83 61)" />
            <use href="#plng-sakura-petal" x="98" y="58" width="10" height="10" opacity="0.55" transform="rotate(35 103 63)" />
            <use href="#plng-spring-flower-sm" x="116" y="52" width="16" height="16" opacity="0.5" transform="rotate(-12 124 60)" />
            <use href="#plng-sakura-petal" x="140" y="56" width="11" height="11" opacity="0.55" transform="rotate(50 145.5 61.5)" />

            {/* Дополнительные мелкие лепестки для заполнения */}
            <g opacity="0.4">
              <use href="#plng-sakura-petal" x="20" y="42" width="8" height="8" transform="rotate(15 24 46)" />
              <use href="#plng-sakura-petal" x="70" y="44" width="8" height="8" transform="rotate(-35 74 48)" />
              <use href="#plng-sakura-petal" x="120" y="42" width="8" height="8" transform="rotate(55 124 46)" />
              <use href="#plng-sakura-petal" x="44" y="18" width="7" height="7" transform="rotate(-50 47.5 21.5)" />
              <use href="#plng-sakura-petal" x="94" y="16" width="7" height="7" transform="rotate(25 97.5 19.5)" />
              <use href="#plng-sakura-petal" x="144" y="18" width="7" height="7" transform="rotate(-10 147.5 21.5)" />
            </g>
          </pattern>
        </defs>

        <rect x="0" y="0" width="100%" height="100%" fill="url(#plng-header-spring-pattern)" />
      </svg>
    </div>
  );
};
